import * as React from 'react';
import { useState } from 'react';
import { Grid, Grow, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import moment from 'moment';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import * as yup from 'yup';

import { Formik } from 'formik';
import {
  AccountData,
  CustomerChoosableResourceData,
  EntityType,
  Language,
  OrderPageData,
  OrderPageTypes,
  RepeatingUnavailabilityRequestData,
  SharedResourceBlocksRequestData,
  UnavailabilityDetailData,
  UnavailabilityService,
} from '../../../../api';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { getOrderPageLabel, UnavailabilityOrderPagesMultiSelect } from './UnavailabilityOrderPagesMultiSelect';
import { RecurringScheduleConfig } from '../../../../common/ui/RecurringScheduleConfig';
import { useUnavailabilities } from '../../../swr/useUnavailabilities';
import { getOrderPages } from '../../../../utils/AccountUtils';
import { DialogState, RecurrentUpdateDialog } from '../../../components/RecurrentUpdateDialog';
import { FormTextField } from '../../../../experienceSettings/formComponents/FormTextField';
import { FormDatePicker } from '../../../../experienceSettings/formComponents/FormDatePicker';
import { FormSwitch } from '../../../../experienceSettings/formComponents/FormSwitch';
import { FormTimePicker } from '../../../../experienceSettings/formComponents/FormTimePicker';
import { areaIdForMask } from '../../../../utils/maskUtils';
import { stripEmptySchedulerBlocks } from './schedulerBlocks';
import { useCalendar } from '../../../CalendarContext/CalendarContext';
import { slotEndSeconds } from '../../../CalendarContext/SlotActionsMenu/CalendarSlot';
import { useLanguage } from '../../../../common/GeneralUtils';
import { getDateFormat, getTimeFormat } from '../../../../common/getDateAndTimeFormat';
import { SchedulerGridDisplayMode, SchedulerResourceGrid } from './SchedulerResourceGrid';
import { WorkshopCircle } from '../../../../common/ui/WorkshopCircle';

export const MAX_PARALLEL_PERSON_CAPACITY = 'max_parallel_person_capacity';
export const CAPACITIES_BLOCKS_ARRAY = 'capacities_blocks_array';
export const STATIONS = 'stations';
const DATE_FORMAT = 'YYYY-MM-DD';

export type UnavailabilityFormValues = {
  all_day: boolean;
  description: string;
  start_date: string;
  end_date: string;
  start_time: number;
  end_time: number;
  unavailability_details?: Array<UnavailabilityDetailData>;
  repeating?: RepeatingUnavailabilityRequestData;
  recurrent_unavailability_id?: string;
  id?: string;
  shared_resources_blocks?: SharedResourceBlocksRequestData;
  max_parallel_workshops?: number;
};
export const PrivateUnavailabilityDrawer = () => {
  const language = useLanguage();

  const {
    closeExperienceAvailabilityDrawer,
    experienceAvailabilityDrawerState: { unavailability, slot },
    startDate: calendarStartDate,
    endDate: calendarEndDate,
  } = useCalendar();
  const [dialogStatus, setDialogStatus] = useState(DialogState.CLOSED);
  const { t } = useTranslation();

  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { longDateFormat } = getDateFormat(account);
  const timeFormat = getTimeFormat(account);
  const orderPages = getOrderPages(account);

  let privateOrdersPages = orderPages.filter((x: OrderPageData) => x.order_page_type === OrderPageTypes.PRIVATE);

  // If account has scheduler configs, filter out order pages that are used in scheduler configs
  // Users should use the scheduler resource grid instead for those order pages
  if (account.shared_resources?.scheduler_configs?.length) {
    const orderPagesInSchedulerConfigs = new Set<string>();

    account.shared_resources.scheduler_configs.forEach((config) => {
      config.allowed_masks?.forEach((mask) => {
        if (mask.order_page_id) {
          orderPagesInSchedulerConfigs.add(mask.order_page_id);
        }
      });
    });

    privateOrdersPages = privateOrdersPages.filter((orderPage) => !orderPagesInSchedulerConfigs.has(orderPage.id));
  }

  // Identify order pages that use customer choosable resources (have CustomResourceDetail)
  const customerChoosableResourceOrderPages = privateOrdersPages.filter(
    (orderPage) => (orderPage.shared_resources_allocations?.customer_choosable_resources?.length || 0) > 0,
  );

  // Filter out customer choosable resource order pages from regular unavailability selection
  // since they're handled at the shared resource level
  const hasCustomerChoosableResources = (account.shared_resources?.customer_choosable_resources?.length || 0) > 0;
  if (hasCustomerChoosableResources) {
    privateOrdersPages = privateOrdersPages.filter(
      (orderPage) => !orderPage.shared_resources_allocations?.customer_choosable_resources?.length,
    );
  }

  const hasRecurrentUnavailability = !!unavailability?.recurrent_unavailability_id;
  const [loading, setLoading] = useState(false);

  const defaultStartMoment = moment().hours(9).minutes(0).seconds(0);
  const startInSec = defaultStartMoment.diff(moment().startOf('day'), 'seconds');
  const defaultEndMoment = moment().hours(17).minutes(0).seconds(0);
  const endInSec = defaultEndMoment.diff(moment().startOf('day'), 'seconds');
  const isUpdatingUnavailability = !!unavailability;
  const getInitialCustomDetails = (orderPageData: OrderPageData) => {
    const orderPage = privateOrdersPages.filter((o: any) => o.id === orderPageData.id)[0];
    const customDetails = {};
    if (orderPage?.custom_details) {
      for (const customDetail of orderPage.custom_details) {
        if (customDetail.cls === 'CustomResourceDetail') {
          // @ts-ignore
          customDetails[customDetail.name] = customDetail.max;
        }
      }
    }

    if (orderPage.availability_settings?.restrictions_obj?.max_parallel_person_capacity?.parallel_persons_capacities) {
      // @ts-ignore
      customDetails[CAPACITIES_BLOCKS_ARRAY] =
        // eslint-disable-next-line max-len
        orderPage.availability_settings?.restrictions_obj?.max_parallel_person_capacity?.parallel_persons_capacities;
    }
    return customDetails;
  };

  const initialValues: UnavailabilityFormValues = isUpdatingUnavailability
    ? {
        description: unavailability.description || t('calendar.unavailability_dialog.descriptionPlaceholder'),
        all_day: unavailability.all_day,
        start_date: unavailability.date,
        end_date: unavailability.date,
        start_time: unavailability.start_time,
        end_time: unavailability.end_time,
        unavailability_details: unavailability.unavailability_details,
        id: unavailability.id,
        recurrent_unavailability_id: unavailability.recurrent_unavailability_id,
        max_parallel_workshops: unavailability.max_parallel_workshops,
        shared_resources_blocks:
          unavailability.shared_resources_blocks ||
          (account.shared_resources?.scheduler_configs?.length
            ? { scheduler_configs: [] }
            : { automatic_dividable_resources: [], customer_choosable_resources: [] }),
      }
    : {
        description: '',
        all_day: !slot,
        start_date: slot?.date ?? moment().startOf('day').format(DATE_FORMAT),
        end_date: slot?.date ?? moment().startOf('day').format(DATE_FORMAT),
        start_time: slot ? slot.startSeconds : startInSec,
        end_time: slot ? slotEndSeconds(slot) : endInSec,
        unavailability_details: privateOrdersPages.map((x: OrderPageData) => ({
          order_page_id: x.id,
          custom_details: getInitialCustomDetails(x),
        })),
        repeating: {
          enabled: false,
          days: [0, 1, 2, 3, 4, 5, 6],
        },
        shared_resources_blocks: account.shared_resources?.scheduler_configs?.length
          ? {
              scheduler_configs: account.shared_resources.scheduler_configs.map((config) => ({
                scheduler_config_id: config.id,
                blocked_mask: '0'.repeat(config.resource_count),
              })),
            }
          : {
              automatic_dividable_resources:
                account.shared_resources?.automatic_dividable_resources?.map((resource) => ({
                  resource_id: resource.id,
                  blocked_capacity: resource.unit_capacity,
                })) || [],
              customer_choosable_resources:
                account.shared_resources?.customer_choosable_resources?.map(
                  (resource: CustomerChoosableResourceData) => ({
                    resource_id: resource.id,
                    blocked_capacity: resource.total_units,
                  }),
                ) || [],
            },
      };
  const validationSchema = yup.object().shape({
    description: yup.string().required(t('calendar.unavailability_dialog.description_required')),
    all_day: yup.boolean(),
    start_date: yup.date().required(t('calendar.unavailability_dialog.date_required')),
    start_time: yup.date().when('allDay', {
      is: false,
      then: yup.date().required(t('calendar.unavailability_dialog.start_time_required')),
    }),
    end_time: yup.date().when('allDay', {
      is: false,
      then: yup.date().required(t('calendar.unavailability_dialog.end_time_required')),
    }),

    unavailability_details: yup.array().of(
      yup.object().shape({
        order_page_id: yup.string().required('Order page ID is required'),
        custom_details: yup.object(),
      }),
    ),
    shared_resources_blocks: yup.object().shape({
      automatic_dividable_resources: yup.array().optional(),
      customer_choosable_resources: yup.array().optional(),
      scheduler_configs: yup.array().optional(),
    }),
  });
  const {
    addItems: addUavailabilities,
    updateItem: updateUavailability,
    reFetch: refetchUnavliability,
  } = useUnavailabilities({
    startDate: calendarStartDate,
    endDate: calendarEndDate,
  });

  const handleClose = () => {
    closeExperienceAvailabilityDrawer();
  };

  const updateUnavliability = async (values: UnavailabilityFormValues) => {
    const updated = await UnavailabilityService.editUnavailability({
      id: values.id!,
      date: values.start_date,
      ...values,
      shared_resources_blocks: stripEmptySchedulerBlocks(values.shared_resources_blocks),
    });
    await updateUavailability(updated);
    setLoading(false);
    setDialogStatus(DialogState.CLOSED);
    handleClose();
  };
  const updateRepeatingUnavliability = async (values: UnavailabilityFormValues) => {
    await UnavailabilityService.editUnavailabilityRecurrent({
      recurrent_unavailability_id: values.recurrent_unavailability_id!,
      date: values.start_date,
      ...values,
      shared_resources_blocks: stripEmptySchedulerBlocks(values.shared_resources_blocks),
    });
    void refetchUnavliability();
    setLoading(false);
    setDialogStatus(DialogState.CLOSED);
    handleClose();
  };
  const createUnavliability = async (values: UnavailabilityFormValues) => {
    if (isUpdatingUnavailability && hasRecurrentUnavailability) {
      setDialogStatus(DialogState.UPDATE);
      return;
    }
    setLoading(true);
    if (isUpdatingUnavailability) {
      await updateUnavliability(values);
    } else {
      const res = await UnavailabilityService.createUnavailability({
        ...values,
        shared_resources_blocks: stripEmptySchedulerBlocks(values.shared_resources_blocks),
      });
      addUavailabilities(res);
      setLoading(false);
      handleClose();
    }
  };
  const onSubmit = async (values: UnavailabilityFormValues) => {
    setLoading(true);
    try {
      if (isUpdatingUnavailability) {
        if (hasRecurrentUnavailability) {
          setDialogStatus(DialogState.UPDATE);
        } else {
          await updateUnavliability(values);
        }
      } else {
        await createUnavliability(values);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Grid style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
        {({ values, handleSubmit, setFieldValue, touched, isSubmitting, submitForm, errors, submitCount }) => {
          return (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Grid
                container
                flexDirection="column"
                flexWrap="nowrap"
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  pt: { xs: '12px', md: '18px' },
                  pb: 2,
                  paddingInline: { xs: '12px', md: '24px' },
                }}>
                <Grid container flexDirection="column" gap={1.5}>
                  <Grid item>
                    <FormTextField
                      width={231}
                      label={t('calendar.unavailability_dialog.description')}
                      fieldName={'description'}
                      isFieldUpdate={isUpdatingUnavailability}
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid>
                    <FormDatePicker
                      disabled={isSubmitting}
                      fieldName={'start_date'}
                      isFieldUpdate={isUpdatingUnavailability}
                      label={t('calendar.unavailability_dialog.date')}
                      onChange={(newValue) => {
                        if (newValue?.isSameOrAfter(values.end_date)) {
                          void setFieldValue('end_date', newValue?.format(DATE_FORMAT));
                        }
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    borderRadius="8px"
                    border="1px solid"
                    borderColor="divider"
                    p={1.5}
                    maxWidth={values.all_day ? '231px' : undefined}>
                    <Grid item>
                      <FormSwitch
                        fieldName={'all_day'}
                        label={t('calendar.unavailability_dialog.all_day')}
                        isFieldUpdate={isUpdatingUnavailability}
                        disabled={isSubmitting}
                      />
                    </Grid>

                    {!values.all_day && (
                      <Grow in={!values.all_day} timeout={1200}>
                        <Grid container gap={2}>
                          <Grid item>
                            <FormTimePicker
                              minTime={moment().set({ hour: 7, minute: 0, second: 0, millisecond: 0 })}
                              label={t('calendar.unavailability_dialog.start_time')}
                              fieldName={'start_time'}
                              disabled={isSubmitting}
                              isFieldUpdate
                            />
                          </Grid>
                          <Grid item>
                            <FormTimePicker
                              minTime={moment().set({ hour: 7, minute: 0, second: 0, millisecond: 0 })}
                              label={t('calendar.unavailability_dialog.end_time')}
                              fieldName={'end_time'}
                              disabled={isSubmitting}
                              isFieldUpdate
                            />
                          </Grid>
                        </Grid>
                      </Grow>
                    )}
                  </Grid>
                  {!isUpdatingUnavailability && (
                    <RecurringScheduleConfig
                      enabled={!!values.repeating?.enabled}
                      onEnabledChange={(checked) => {
                        void setFieldValue('repeating.enabled', checked);
                        if (!checked) {
                          void setFieldValue('end_date', moment(values.start_date).startOf('day').format(DATE_FORMAT));
                        }
                      }}
                      startDate={moment(values.start_date)}
                      onChange={(val) => {
                        if (val) {
                          void setFieldValue('repeating.days', val.days);
                          void setFieldValue('end_date', val.end_date);
                        }
                      }}
                      label={t('calendar.unavailability_dialog.repeating')}
                      initialDays={initialValues.repeating?.days}
                    />
                  )}

                  {privateOrdersPages && (
                    <Grid container flexDirection="column" mt={1.5} gap={1.5} maxWidth={'400px'}>
                      <Grid item>
                        <Typography variant="h5">
                          {t('calendar.unavailability_dialog.max_parallel_workshops')}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <FormTextField
                          type="number"
                          width={100}
                          fieldName="max_parallel_workshops"
                          isFieldUpdate={isUpdatingUnavailability}
                          disabled={isSubmitting}
                          min={0}
                        />
                      </Grid>
                      {account.shared_resources && (
                        <Grid container flexDirection="column" mt={1.5} gap={1.5} maxWidth={'400px'}>
                          <Grid item>
                            <Typography variant="h5">{t('calendar.unavailability_dialog.resources')}</Typography>
                          </Grid>

                          {account.shared_resources.scheduler_configs?.length ? (
                            <Grid container flexDirection="column" gap={2}>
                              {account.shared_resources.scheduler_configs.map((config) => (
                                <SchedulerResourceGrid
                                  key={config.id}
                                  schedulerConfig={config}
                                  blockedMask={
                                    values.shared_resources_blocks?.scheduler_configs?.find(
                                      (block) => block.scheduler_config_id === config.id,
                                    )?.blocked_mask || '0'.repeat(config.resource_count)
                                  }
                                  onChange={(mask) => {
                                    const currentSchedulerConfigs =
                                      values.shared_resources_blocks?.scheduler_configs || [];
                                    // Keep area_id in step with the mask: the day view lists and
                                    // lifts blocks per area, so a block written without it is
                                    // invisible there even though it still removes capacity.
                                    const updatedBlock = {
                                      scheduler_config_id: config.id,
                                      blocked_mask: mask,
                                      area_id: areaIdForMask(mask, config.areas ?? []),
                                    };
                                    const existingIndex = currentSchedulerConfigs.findIndex(
                                      (block) => block.scheduler_config_id === config.id,
                                    );
                                    const updatedConfigs =
                                      existingIndex === -1
                                        ? [...currentSchedulerConfigs, updatedBlock]
                                        : currentSchedulerConfigs.map((block, position) =>
                                            position === existingIndex ? updatedBlock : block,
                                          );
                                    void setFieldValue('shared_resources_blocks.scheduler_configs', updatedConfigs);
                                  }}
                                  disabled={isSubmitting}
                                  displayMode={SchedulerGridDisplayMode.FULL}
                                />
                              ))}
                            </Grid>
                          ) : (
                            <Grid container flexDirection="column" gap={2}>
                              {account.shared_resources.automatic_dividable_resources?.map((resource) => (
                                <Grid item key={resource.id}>
                                  <FormTextField
                                    type="number"
                                    width={100}
                                    label={resource.label[language] || resource.label.he}
                                    fieldName={`shared_resources_blocks.automatic_dividable_resources[${
                                      values.shared_resources_blocks?.automatic_dividable_resources?.findIndex(
                                        (r) => r.resource_id === resource.id,
                                      ) ?? 0
                                    }].blocked_capacity`}
                                    isFieldUpdate={isUpdatingUnavailability}
                                    disabled={isSubmitting}
                                    min={0}
                                    max={resource.unit_capacity}
                                  />
                                </Grid>
                              ))}
                              {(account.shared_resources?.customer_choosable_resources?.length || 0) > 0 && (
                                <>
                                  <Grid item mt={2}>
                                    {customerChoosableResourceOrderPages.length > 0 && (
                                      <Grid container alignItems="center" gap={1} sx={{ mt: 0.5 }}>
                                        {customerChoosableResourceOrderPages.map((orderPage) => (
                                          <Grid container alignItems="center" gap={1} key={orderPage.id}>
                                            <WorkshopCircle
                                              key={orderPage.id}
                                              account={account}
                                              orderPageId={orderPage.id}
                                              language={language as 'he' | 'en'}
                                            />
                                            <Grid item>{getOrderPageLabel(language, orderPages, orderPage.id)}</Grid>
                                          </Grid>
                                        ))}
                                      </Grid>
                                    )}
                                  </Grid>
                                  {account.shared_resources?.customer_choosable_resources?.map(
                                    (resource: CustomerChoosableResourceData, index: number) => {
                                      const existingIndex =
                                        values.shared_resources_blocks?.customer_choosable_resources?.findIndex(
                                          (r) => r.resource_id === resource.id,
                                        );
                                      const resourceIndex = existingIndex && existingIndex > -1 ? existingIndex : index;
                                      const currentValue =
                                        values.shared_resources_blocks?.customer_choosable_resources?.[resourceIndex]
                                          ?.blocked_capacity || 0;
                                      const totalUnits = resource.total_units || 999;
                                      const label = resource.label?.[language] || resource.id;

                                      return (
                                        <Grid item key={resource.id}>
                                          <Grid container gap={1} alignItems="center" py={1}>
                                            <Grid item>
                                              <Typography variant="body1">{label}:</Typography>
                                            </Grid>
                                            {totalUnits && totalUnits < 6 ? (
                                              <ToggleButtonGroup
                                                value={currentValue}
                                                exclusive
                                                onChange={(_, newValue) => {
                                                  if (newValue !== null) {
                                                    void setFieldValue(
                                                      `shared_resources_blocks.customer_choosable_resources[${resourceIndex}]`,
                                                      {
                                                        resource_id: resource.id,
                                                        blocked_capacity: newValue,
                                                      },
                                                    );
                                                  }
                                                }}
                                                aria-label={label}
                                                sx={{ direction: language === Language.HE ? 'rtl' : undefined }}>
                                                <Grid container gap={0.3}>
                                                  {[...Array(totalUnits + 1)].map((_, btnIndex) => (
                                                    <ToggleButton
                                                      key={btnIndex}
                                                      value={btnIndex}
                                                      aria-label={`${btnIndex}`}
                                                      sx={{ width: '40px' }}
                                                      disabled={isSubmitting}>
                                                      {btnIndex}
                                                    </ToggleButton>
                                                  ))}
                                                </Grid>
                                              </ToggleButtonGroup>
                                            ) : (
                                              <TextField
                                                size="small"
                                                type="number"
                                                InputProps={{
                                                  inputProps: { min: 0, max: totalUnits },
                                                }}
                                                value={currentValue}
                                                onChange={(e) => {
                                                  void setFieldValue(
                                                    `shared_resources_blocks.customer_choosable_resources[${resourceIndex}]`,
                                                    {
                                                      resource_id: resource.id,
                                                      blocked_capacity: parseInt(e.target.value, 10) || 0,
                                                    },
                                                  );
                                                }}
                                                disabled={isSubmitting}
                                              />
                                            )}
                                          </Grid>
                                        </Grid>
                                      );
                                    },
                                  )}
                                </>
                              )}
                            </Grid>
                          )}
                        </Grid>
                      )}
                      {privateOrdersPages.length > 0 && (
                        <>
                          <Grid item mb={-2}>
                            <Typography variant="h5">{account.activity_label_plural?.[language]}:</Typography>
                          </Grid>
                          <UnavailabilityOrderPagesMultiSelect
                            orderPages={privateOrdersPages}
                            unavailabilityDetails={values.unavailability_details || []}
                            setUnavailabilityDetails={(selected: UnavailabilityDetailData[]) =>
                              setFieldValue('unavailability_details', selected)
                            }
                            isFieldUpdate={isUpdatingUnavailability}
                          />
                        </>
                      )}
                    </Grid>
                  )}
                </Grid>

                {/* Timestamp information - show when editing existing unavailability */}
                {unavailability && (unavailability.created_at || unavailability.updated_at) && (
                  <Grid
                    container
                    mt={2}
                    px={2}
                    py={1.5}
                    border="1px solid"
                    borderColor="divider"
                    borderRadius="8px"
                    bgcolor="mb.ground"
                    flexDirection="column"
                    maxWidth="400px">
                    <Typography variant="caption" color="text.secondary" fontWeight="bold" mb={0.5} display="block">
                      {t('calendar.note_dialog.timestamps')}
                    </Typography>
                    {unavailability.created_at && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>{t('calendar.note_dialog.created_at')}:</strong>{' '}
                        {moment.utc(unavailability.created_at).local().format(longDateFormat)}{' '}
                        {t('calendar.note_dialog.at')}{' '}
                        {moment.utc(unavailability.created_at).local().format(timeFormat)}
                      </Typography>
                    )}
                    {unavailability.updated_at && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>{t('calendar.note_dialog.updated_at')}:</strong>{' '}
                        {moment.utc(unavailability.updated_at).local().format(longDateFormat)}{' '}
                        {t('calendar.note_dialog.at')}{' '}
                        {moment.utc(unavailability.updated_at).local().format(timeFormat)}
                      </Typography>
                    )}
                  </Grid>
                )}

                {dialogStatus !== DialogState.CLOSED && (
                  <RecurrentUpdateDialog
                    dialogState={dialogStatus}
                    setDialogStatus={setDialogStatus}
                    recurrentId={unavailability?.recurrent_unavailability_id}
                    recurrentEntityType={EntityType.UNAVAILABILITY}
                    updateSingle={() => updateUnavliability(values)}
                    updateRecurrent={() => updateRepeatingUnavliability(values)}
                    deleteSingle={() =>
                      new Promise(() => {
                        console.log('Only update here');
                      })
                    }
                    deleteReccurrent={() =>
                      new Promise(() => {
                        console.log('Only update here');
                      })
                    }
                    texts={{
                      update: {
                        cta: t('calendar.unavailability_dialog.edit_cta'),
                        title: t('calendar.unavailability_dialog.edit_title_questions'),
                        singleOption: t('calendar.unavailability_dialog.single'),
                        recurrentOption: t('calendar.unavailability_dialog.repeatingUpdate'),
                        recurrentCount: t('calendar.delete_unavailability.recurrent_count'),
                      },
                      delete: {
                        title: 'Delete is not supported yet for this dialog',
                        singleOption: 'Delete is not supported yet for this dialog',
                        recurrentOption: 'Delete is not supported yet for this dialog',
                        recurrentCount: 'Delete is not supported yet for this dialog',
                      },
                    }}
                  />
                )}
              </Grid>
              <Grid
                container
                justifyContent="flex-end"
                alignItems="center"
                gap={1}
                sx={{
                  flexShrink: 0,
                  py: 1.5,
                  paddingInline: { xs: '12px', md: '24px' },
                  borderTop: '1px solid',
                  borderColor: 'mb.lineSoft',
                }}>
                {submitCount > 0 && Object.keys(errors).length > 0 && (
                  <Typography variant="body2" color="error">
                    {Object.values(errors).flat().join(', ')}
                  </Typography>
                )}
                <LoadingButton
                  onClick={submitForm}
                  loading={loading}
                  variant="contained"
                  sx={{ textTransform: 'none', minWidth: 100 }}
                  disabled={!touched}>
                  {t('save')}
                </LoadingButton>
              </Grid>
            </form>
          );
        }}
      </Formik>
    </Grid>
  );
};
